import React from "react";
import PropTypes from "prop-types";
import { Card, CardContent, Box, Typography, Chip, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

/**
 * Compact patient card rendered in patient list.
 */
function PatientCard({ patient, onClick, selected, organization, onDelete }) {
  const handleDelete = (e) => {
    e.stopPropagation(); // Prevent card click
    if (onDelete) {
      onDelete(patient.id);
    }
  };

  return (
    <Card
      sx={{
        mb: 1,
        cursor: "pointer",
        bgcolor: selected ? "primary.main" : "background.paper",
        color: selected ? "primary.contrastText" : "text.primary",
        "&:hover": {
          bgcolor: selected ? "primary.dark" : "action.hover",
        },
      }}
      onClick={() => onClick(patient)}
    >
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Typography variant="subtitle2">{patient.name}</Typography>
            <Typography
              variant="caption"
              color={selected ? "primary.contrastText" : "text.secondary"}
            >
              MRN: {patient.mrn} • {patient.sex} • DOB: {patient.dob}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {organization && (
              <Chip label={organization.name} size="small" color="primary" variant="outlined" />
            )}
            {onDelete && (
              <IconButton
                size="small"
                onClick={handleDelete}
                sx={{
                  color: selected ? "primary.contrastText" : "error.main",
                  "&:hover": {
                    bgcolor: "error.dark",
                    color: "white",
                  },
                }}
                data-action="delete-patient"
                data-patient-id={patient.id}
                aria-label={`Delete patient ${patient.name}`}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

PatientCard.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    mrn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sex: PropTypes.string,
    dob: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  organization: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
  }),
  onDelete: PropTypes.func,
};

PatientCard.defaultProps = {
  selected: false,
  organization: null,
  onDelete: null,
};

export default PatientCard;
